"use client";

import { useEffect } from "react";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "wide" | "xl";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = "";
    };
  }, [open, onClose]);

  const sizes = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", wide: "max-w-4xl", xl: "max-w-6xl" };

  return (
    <>
      {open && (
        <>
          <div
            key="backdrop"
            onClick={onClose}
            className="modal-backdrop-in fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm"
            aria-hidden
          />
          <div className="fixed inset-0 z-[65] grid grid-cols-[minmax(0,1fr)] place-items-center overflow-y-auto p-2 sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
            <div
              className={cn("modal-in relative my-4 w-full min-w-0 sm:my-8 rounded-portal border border-line/60 bg-white shadow-lift", sizes[size])}
            >
              <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-4 sm:px-6 sm:py-5">
                <div className="min-w-0 [overflow-wrap:anywhere]">
                  <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
                  {description && <p className="mt-0.5 text-sm text-ink-soft">{description}</p>}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="rounded-lg p-1.5 text-ink-muted transition-colors hover:bg-cream hover:text-ink"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-4 py-4 [overflow-wrap:anywhere] sm:px-6 sm:py-5">{children}</div>
              {footer && <div className="flex justify-end gap-2 border-t border-line px-4 py-4 sm:px-6 sm:py-5">{footer}</div>}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  danger = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-portal-sm border border-line px-4 text-sm font-medium text-ink-soft transition-colors hover:bg-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={cn(
              "h-10 rounded-portal-sm px-4 text-sm font-semibold text-white shadow-soft transition-colors",
              danger ? "bg-[#b3261e] hover:bg-[#8f1d17]" : "bg-brand-600 hover:bg-brand-700"
            )}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-ink-soft">{message}</p>
    </Modal>
  );
}
